import { DashboardOverviewGraph } from '@/components/dashboard-overview-graph';
import { get } from '@/lib/api';
import { formatRelativeTime } from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { useQuery } from '@tanstack/react-query';
import type { DashboardOverviewResponse, OnboardingStatus, PostureSummary } from '@uncain/shared';
import { AlertTriangle, Box, GitBranch, Shield } from 'lucide-react';

function StatCard({
	label,
	value,
	icon: Icon,
	color = 'text-foreground',
}: {
	label: string;
	value: string | number;
	icon: React.ComponentType<{ className?: string }>;
	color?: string;
}) {
	return (
		<div className="rounded-lg border border-border bg-card p-5">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm text-muted-foreground">{label}</p>
					<p className={`mt-1 text-2xl font-semibold ${color}`}>{value}</p>
				</div>
				<Icon className="h-8 w-8 text-muted-foreground/50" />
			</div>
		</div>
	);
}

export function DashboardPage() {
	const posture = useQuery<PostureSummary>({
		queryKey: queryKeys.posture.summary(),
		queryFn: () => get<PostureSummary>('/posture/summary'),
	});

	const onboarding = useQuery<OnboardingStatus>({
		queryKey: queryKeys.org.onboarding(),
		queryFn: () => get<OnboardingStatus>('/org/onboarding'),
	});

	const overview = useQuery<DashboardOverviewResponse>({
		queryKey: queryKeys.dashboard.overview(),
		queryFn: () => get<DashboardOverviewResponse>('/dashboard/overview'),
		enabled: onboarding.data?.phase === 'complete',
	});

	if (posture.isLoading || onboarding.isLoading || overview.isLoading) {
		return <div className="text-muted-foreground">Loading dashboard...</div>;
	}

	// Show onboarding prompt for new orgs
	if (onboarding.data?.phase === 'pending') {
		return (
			<div className="space-y-6">
				<h1 className="text-2xl font-bold text-foreground">Welcome to Uncain</h1>
				<div className="rounded-lg border border-border bg-card p-8 text-center">
					<Shield className="mx-auto h-12 w-12 text-primary" />
					<h2 className="mt-4 text-lg font-semibold text-foreground">Install the GitHub App</h2>
					<p className="mt-2 text-muted-foreground">
						To get started, install the Uncain GitHub App on your organization. We&apos;ll scan your
						workflows and build your trust graph automatically.
					</p>
				</div>
			</div>
		);
	}

	if (onboarding.data?.phase === 'scanning') {
		return (
			<div className="space-y-6">
				<h1 className="text-2xl font-bold text-foreground">Scanning in Progress</h1>
				<div className="rounded-lg border border-border bg-card p-8 text-center">
					<div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					<h2 className="mt-4 text-lg font-semibold text-foreground">Building your trust graph</h2>
					<p className="mt-2 text-muted-foreground">
						{onboarding.data.repoCount} repositories discovered.
						{onboarding.data.workflowCount > 0
							? ` ${onboarding.data.workflowCount} workflows parsed.`
							: ' Parsing workflows...'}
					</p>
				</div>
			</div>
		);
	}

	const data = posture.data;

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
				{data?.lastUpdatedAt && (
					<span className="text-sm text-muted-foreground">
						Updated {formatRelativeTime(data.lastUpdatedAt)}
					</span>
				)}
			</div>

			{/* Stat cards — trust graph summary folded into the top row */}
			<div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
				<StatCard label="Repositories" value={data?.repoCount ?? 0} icon={GitBranch} />
				<StatCard label="Workflows" value={data?.workflowCount ?? 0} icon={Box} />
				<StatCard
					label="Open Incidents"
					value={data?.incidentCount ?? 0}
					icon={AlertTriangle}
					color={data?.incidentCount ? 'text-severity-critical' : 'text-foreground'}
				/>
				<StatCard
					label="Quarantined"
					value={data?.quarantineCount ?? 0}
					icon={Shield}
					color={data?.quarantineCount ? 'text-severity-high' : 'text-foreground'}
				/>
				<StatCard label="Actions" value={data?.actionCount ?? 0} icon={Box} />
				<StatCard label="Trust Edges" value={data?.edgeCount ?? 0} icon={GitBranch} />
			</div>

			{overview.data ? (
				<DashboardOverviewGraph overview={overview.data} />
			) : (
				<div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
					Overview data will appear after the next sync.
				</div>
			)}
		</div>
	);
}
